import { UpdateMassTortModal } from "@/components/features/mass-torts/UpdateMassTortModal";
import {
  ContextMenu,
  ContextMenuItem,
  ContextMenuSeparator,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { retrieveMassTortQueryOptions } from "@/lib/data/react-query/mass-torts/mass-tort";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { copyToClipboard } from "@/lib/utils/copyToClipboard";
import { useQuery } from "@tanstack/react-query";
import { Clipboard, ExternalLink, Pencil } from "lucide-react";
import { useState, type ReactNode } from "react";

type MassTortRowContextMenuProps = {
  massTortId: string;
  title: string;
  trigger: ReactNode;
};

export function MassTortRowContextMenu({
  massTortId,
  title,
  trigger,
}: MassTortRowContextMenuProps) {
  const { hasActionPermission } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const { data: massTortData } = useQuery({
    ...retrieveMassTortQueryOptions(massTortId),
    enabled: isEditing,
  });
  const canEdit = hasActionPermission(
    ACTION_PERMISSION_KEYS.massTort,
    ACTION_PERMISSION_VALUES.update,
  );

  return (
    <>
      <ContextMenu entityName="mass tort" trigger={trigger}>
        <ContextMenuItem
          onSelect={() => {
            window.open(
              `/mass-torts/${massTortId}`,
              "_blank",
              "noopener,noreferrer",
            );
          }}
        >
          <ExternalLink />
          Open in new tab
        </ContextMenuItem>
        <ContextMenuItem
          onSelect={() => {
            void copyToClipboard(title, "Mass tort title");
          }}
        >
          <Clipboard />
          Copy mass tort title
        </ContextMenuItem>
        {canEdit ? (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              onSelect={() => {
                setIsEditing(true);
              }}
            >
              <Pencil />
              Edit mass tort
            </ContextMenuItem>
          </>
        ) : null}
      </ContextMenu>

      {massTortData?.data ? (
        <UpdateMassTortModal
          open={isEditing}
          setOpen={setIsEditing}
          massTort={massTortData.data}
          massTortId={massTortId}
        />
      ) : null}
    </>
  );
}
